import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api, { API } from '@/lib/api';
import { openVideoWS } from '@/lib/upload';
import { useT } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { logDebug } from '@/lib/devlog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Share2, TrendingUp, Eye, Clock, Trash2, Sparkles, Loader2, RefreshCw, Send } from 'lucide-react';
import PublishModal from './social/PublishModal';
import StatChip from '@/components/premium/StatChip';
import { SkeletonClipTile } from '@/components/premium/Skeleton';

const formatEtaDuration = (seconds, lang) => {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return lang === 'fr' ? `${m}min ${rem}s` : `${m}m ${rem}s`;
};

const ProcessingBlock = ({ stage, progress, lang }) => {
  const [startedAt] = useState(() => Date.now());
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => { setNowTs(Date.now()); }, 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = Math.max(0, (nowTs - startedAt) / 1000);
  let eta;
  if (progress < 5) eta = null;
  else if (progress >= 100) eta = 0;
  else eta = elapsed * (100 - progress) / progress;
  return (
    <div className="mb-6 p-5 rounded-2xl border border-[#D4FF00]/30 bg-[#D4FF00]/5 flex items-center gap-4" data-testid="video-live-progress">
      <Loader2 className="w-6 h-6 text-[#D4FF00] animate-spin shrink-0" />
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1.5 gap-3">
          <span className="text-sm font-mono text-[#D4FF00] truncate">{stage}</span>
          <span className="text-xs font-mono text-zinc-400 shrink-0">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2 bg-white/5" />
        <div className="flex items-center justify-between mt-2 text-xs text-zinc-500 gap-3">
          <span>{lang === 'fr' ? "L'IA analyse et découpe les meilleurs moments en temps réel." : 'AI is analyzing and cutting the best moments live.'}</span>
          <span data-testid="video-eta" className="flex items-center gap-1.5 font-mono shrink-0">
            <Clock className="w-3 h-3" />
            {eta === null ? (lang === 'fr' ? 'estimation…' : 'estimating…')
              : eta === 0 ? (lang === 'fr' ? 'finalisation…' : 'finalizing…')
              : (
                <>
                  <span className="text-zinc-300">{lang === 'fr' ? `~${formatEtaDuration(eta, lang)} restant` : `~${formatEtaDuration(eta, lang)} left`}</span>
                  <span className="text-zinc-600 hidden sm:inline">· {formatEtaDuration(elapsed, lang)}</span>
                </>
              )}
          </span>
        </div>

        {/* Reassurance — quality-first framing, no fake ETA, no force-retry CTA */}
        {progress < 100 && (
          <div
            className="mt-3 flex items-start gap-2.5 text-xs text-zinc-400 leading-relaxed"
            data-testid="processing-reassurance"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF00]/60 mt-1.5 shrink-0 animate-pulse" />
            <span>
              {lang === 'fr'
                ? 'Le traitement peut prendre quelques instants. Nous analysons votre vidéo en profondeur afin de sélectionner les meilleurs moments et garantir une meilleure qualité de clips.'
                : 'Processing may take a moment. We analyse your video in depth to pick the best moments and deliver higher-quality clips.'}
              <span className="block text-zinc-500 mt-1">
                {lang === 'fr'
                  ? 'Les vidéos longues peuvent nécessiter davantage de temps de traitement.'
                  : 'Longer videos may require more processing time.'}
              </span>
              <span className="block text-zinc-500 mt-1">
                {lang === 'fr' ? 'En cas de problème : ' : 'If anything goes wrong: '}
                <a
                  href="mailto:contact@highlightai.online"
                  className="text-zinc-300 hover:text-[#D4FF00] underline decoration-white/10 hover:decoration-[#D4FF00]/40 font-mono"
                  data-testid="processing-support-email"
                >
                  contact@highlightai.online
                </a>
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const formatLabel = { tiktok: '9:16 TikTok', shorts: '9:16 Shorts', reels: '9:16 Reels', horizontal: '16:9 YouTube' };

const ClipCard = ({ clip, idx }) => {
  const navigate = useNavigate();
  const { t } = useT();
  const [publishOpen, setPublishOpen] = useState(false);

  const downloadClip = async (e) => {
    e.stopPropagation();
    if (!clip.has_file) { toast.error('File not ready yet'); return; }
    try {
      // httpOnly cookie auth is sent automatically (same-origin).
      const resp = await fetch(`${API}/clips/${clip.id}/download`, {
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `highlightai_${clip.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('vc.export.started'));
    } catch (err) {
      toast.error(err.message || 'Download failed');
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }} className="group rounded-2xl border border-white/5 bg-white/[0.02] hover:border-[#D4FF00]/40 overflow-hidden transition" data-testid={`clip-card-${clip.id}`}>
      <div className="relative aspect-[9/16] overflow-hidden cursor-pointer" onClick={() => navigate(`/clips/${clip.id}`)} style={{ background: `linear-gradient(135deg, ${clip.thumbnail_color}, #050505)` }}>
        {clip.thumbnail_path && (
          <img
            src={`${API}/clips/${clip.id}/thumbnail`}
            alt={clip.title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            data-testid={`clip-thumbnail-${clip.id}`}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40 pointer-events-none" />
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `radial-gradient(circle at 30% 30%, ${clip.thumbnail_color}, transparent 50%)` }} />
        <div className="absolute top-3 left-3 right-3 flex justify-between">
          <span className="text-[10px] uppercase tracking-widest font-mono text-white/70">{formatLabel[clip.format]}</span>
          <span className="acid-chip px-2 py-0.5 rounded text-[10px] font-bold">{clip.viral_score} VIRAL</span>
        </div>
        <div className="absolute bottom-12 left-3 right-3">
          <p className="tiktok-caption text-base text-white">{clip.caption_preview}</p>
        </div>
        <div className="absolute bottom-2 left-3 right-3 flex justify-between text-[10px] text-white/70 font-mono">
          <span>{fmtTime(clip.start)}</span>
          <span>{Math.round(clip.duration)}s</span>
          <span>{fmtTime(clip.end)}</span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px] uppercase tracking-wider font-mono">{clip.category}</Badge>
          <span className="text-[10px] text-zinc-500 font-mono">{new Date(clip.created_at || Date.now()).toLocaleDateString()}</span>
        </div>
        <h3 className="font-heading text-base font-bold mb-3 line-clamp-2 leading-tight">{clip.title}</h3>
        {/* Score chips — Hook / Emotion / Retention / Virality at a glance */}
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`clip-chips-${clip.id}`}>
          <StatChip label="Hook" value={Math.max(0, Math.round((clip.viral_score || 0) * 0.92))} axis="hook" testid={`clip-chip-hook-${clip.id}`} />
          <StatChip label="Emo" value={clip.engagement_score} axis="emotion" testid={`clip-chip-emotion-${clip.id}`} />
          <StatChip label="Ret" value={clip.retention_score} axis="retention" suffix="%" testid={`clip-chip-retention-${clip.id}`} />
          <StatChip label="Viral" value={clip.viral_score} axis="viral" testid={`clip-chip-viral-${clip.id}`} />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1 border-white/10 bg-transparent text-xs hover:bg-[#D4FF00] hover:text-black hover:border-[#D4FF00]" data-testid={`clip-download-${clip.id}`} onClick={downloadClip} disabled={!clip.has_file}><Download className="w-3 h-3 mr-1" /> {t('vc.export')}</Button>
          <Button size="sm" className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 text-xs font-semibold" data-testid={`clip-publish-${clip.id}`} onClick={(e) => { e.stopPropagation(); setPublishOpen(true); }} disabled={!clip.has_file}><Send className="w-3 h-3 mr-1" /> Publish</Button>
          <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-[#D4FF00]" data-testid={`clip-share-${clip.id}`} onClick={() => toast.info(t('vc.share.toast'))}><Share2 className="w-3 h-3" /></Button>
        </div>
      </div>
      <PublishModal clip={clip} open={publishOpen} onClose={() => setPublishOpen(false)} />
    </motion.div>
  );
};

const VideoClips = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const { user } = useAuth();
  const [video, setVideo] = useState(null);
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveProgress, setLiveProgress] = useState({ stage: null, progress: 0 });
  const wsRef = useRef(null);
  const isPaidPlan = useMemo(
    () => ['creator', 'studio', 'pro', 'premium'].includes(user?.plan),
    [user?.plan],
  );

  const refetch = useCallback(async () => {
    try {
      const [v, c] = await Promise.all([
        api.get(`/videos/${id}`),
        api.get(`/videos/${id}/clips`),
      ]);
      setVideo(v.data);
      setClips(c.data);
    } catch {
      toast.error(t('vc.notfound'));
    }
  }, [id, t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [v, c] = await Promise.all([
          api.get(`/videos/${id}`),
          api.get(`/videos/${id}/clips`),
        ]);
        if (cancelled) return;
        setVideo(v.data);
        setClips(c.data);
      } catch {
        if (!cancelled) toast.error(t('vc.notfound'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, t]);

  useEffect(() => {
    if (!video || video.status === 'done' || video.status === 'failed') return;
    if (wsRef.current) return;
    const { ws, close } = openVideoWS(id, (msg) => {
      if (msg.type === 'progress' || msg.type === 'state') {
        setLiveProgress({ stage: msg.stage, progress: msg.progress || 0 });
      }
      if (msg.type === 'done') {
        setLiveProgress({ stage: 'Done', progress: 100 });
        refetch();
        close();
        wsRef.current = null;
      }
      if (msg.type === 'error') {
        toast.error(msg.message || 'Processing failed');
        refetch();
        close();
        wsRef.current = null;
      }
    });
    wsRef.current = ws;
    return () => {
      try {
        ws.close();
      } catch (e) {
        logDebug('[ws] close on unmount failed (likely already closed):', e?.message);
      }
      wsRef.current = null;
    };
  }, [video, id, refetch]);

  const removeVideo = async () => {
    if (!window.confirm(t('vc.delete.confirm'))) return;
    await api.delete(`/videos/${id}`);
    toast.success(t('vc.deleted'));
    navigate('/dashboard');
  };

  const reanalyze = async () => {
    try {
      await api.post(`/videos/${id}/reanalyze`);
      toast.success(lang === 'fr' ? 'Réanalyse lancée avec l\'IA' : 'AI re-analysis started');
      setClips([]);
      setLiveProgress({ stage: 'Re-queued', progress: 0 });
      await refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-analyze failed');
    }
  };

  const shareClip = (clipId) => {
    const url = `${window.location.origin}/share/${clipId}`;
    if (navigator.share) {
      navigator.share({ title: 'Mon clip viral', url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast.success(lang === 'fr' ? 'Lien copié 🔗' : 'Link copied 🔗');
    }
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="video-clips-loading">
      <div className="h-6 w-32 skeleton-shimmer rounded mb-6" />
      <div className="h-10 w-2/3 skeleton-shimmer rounded mb-10" />
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: 6 }).map((_, i) => <SkeletonClipTile key={i} />)}
      </div>
    </div>
  );
  if (!video) return <div className="p-10 text-zinc-500">{t('vc.notfound')}</div>;

  const avgViral = clips.length ? Math.round(clips.reduce((a, c) => a + c.viral_score, 0) / clips.length) : 0;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="video-clips-page">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-6"><ArrowLeft className="w-4 h-4" /> {t('vc.back')}</Link>

      {video.status === 'processing' && (
        <ProcessingBlock
          stage={liveProgress.stage || video.stage || 'Queued'}
          progress={liveProgress.progress || video.progress || 0}
          lang={lang}
        />
      )}

      {video.status === 'failed' && (
        <div className="mb-6 p-5 rounded-2xl border border-red-500/30 bg-red-500/5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="video-failed">
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-red-300 mb-1">{lang === 'fr' ? 'Traitement échoué' : 'Processing failed'}</h3>
            <p className="text-sm text-zinc-400 font-mono">{video.error || 'Unknown error'}</p>
          </div>
          {video.storage_path !== null && (
            <Button onClick={reanalyze} className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 shrink-0" data-testid="reanalyze-failed-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              {lang === 'fr' ? 'Relancer le traitement' : 'Retry processing'}
            </Button>
          )}
        </div>
      )}

      {video.status === 'done' && video.ai_used === false && video.ai_error === 'budget' && (
        <div className="mb-6 p-5 rounded-2xl border border-red-500/30 bg-red-500/5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="budget-exceeded-banner">
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-red-300 mb-1">{lang === 'fr' ? 'Budget IA épuisé' : 'AI budget exhausted'}</h3>
            <p className="text-sm text-zinc-400">
              {lang === 'fr'
                ? 'Ta clé Emergent LLM n\'a plus de crédit. Recharge-la pour que l\'IA analyse réellement ta vidéo. Va dans Profil → Universal Key → Add Balance, puis clique sur Réanalyser ici.'
                : 'Your Emergent LLM key is out of credit. Top it up in Profile → Universal Key → Add Balance, then click Re-analyze here.'}
            </p>
          </div>
          <Button onClick={reanalyze} className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 shrink-0" data-testid="reanalyze-after-topup-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            {lang === 'fr' ? 'Réanalyser après recharge' : 'Re-analyze after top-up'}
          </Button>
        </div>
      )}

      {video.status === 'done' && video.ai_used === false && video.ai_error !== 'budget' && (
        <div className="mb-6 p-5 rounded-2xl border border-orange-500/30 bg-orange-500/5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="demo-mode-banner">
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-orange-300 mb-1">{lang === 'fr' ? 'Clips générés en mode démo' : 'Clips generated in demo mode'}</h3>
            <p className="text-sm text-zinc-400">
              {lang === 'fr'
                ? 'L\'IA n\'a pas analysé cette vidéo (problème transitoire au moment de l\'upload). Les sous-titres et timestamps sont aléatoires. Clique sur Réanalyser pour relancer le vrai pipeline Whisper + GPT.'
                : 'AI didn\'t analyze this video (transient issue at upload time). Captions and timestamps are random. Click Re-analyze to run the real Whisper + GPT pipeline.'}
            </p>
          </div>
          <Button onClick={reanalyze} className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 shrink-0" data-testid="reanalyze-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            {lang === 'fr' ? 'Réanalyser avec IA' : 'Re-analyze with AI'}
          </Button>
        </div>
      )}

      {video.status === 'done' && !isPaidPlan && clips.length > 0 && (
        <div className="mb-6 p-4 rounded-2xl border border-[#D4FF00]/30 bg-gradient-to-r from-[#D4FF00]/[0.06] to-transparent flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="watermark-upgrade-banner">
          <div className="flex-1">
            <h3 className="font-heading text-base font-bold text-[#D4FF00] mb-1 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              {lang === 'fr' ? 'Tes clips ont un filigrane HighlightAI' : 'Your clips carry a HighlightAI watermark'}
            </h3>
            <p className="text-sm text-zinc-400">
              {lang === 'fr'
                ? 'Passe en Pro pour exporter sans filigrane, et obtiens aussi l\'export 1080p HD + traitement prioritaire.'
                : 'Upgrade to Pro to export without watermark, plus get 1080p HD export and priority processing.'}
            </p>
          </div>
          <Button onClick={() => navigate('/pricing')} className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 shrink-0" data-testid="upgrade-remove-watermark-btn">
            {lang === 'fr' ? 'Passer en Pro' : 'Upgrade to Pro'}
          </Button>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6 mb-10">
        <div className="lg:col-span-8 p-6 rounded-2xl border border-white/5 bg-gradient-to-br from-white/[0.04] to-transparent">
          <span className="text-xs uppercase tracking-[0.2em] text-[#D4FF00] font-mono">{t('vc.source')}</span>
          <h1 className="font-heading text-3xl lg:text-4xl font-black mt-2 mb-3 tracking-tight" data-testid="video-title">{video.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400 font-mono">
            <span>{video.source_type}</span><span>·</span>
            <span>{Math.round(video.duration/60)} {t('vc.min')}</span><span>·</span>
            <span>{(video.size/(1024*1024)).toFixed(1)} MB</span><span>·</span>
            <span>{t('vc.processed', { t: video.processing_time })}</span>
            {video.ai_used ? (
              <span className="px-2 py-0.5 rounded acid-chip text-[10px] uppercase tracking-wider" data-testid="badge-ai-real">AI · Whisper + GPT</span>
            ) : video.status === 'done' ? (
              <span className="px-2 py-0.5 rounded orange-chip text-[10px] uppercase tracking-wider" data-testid="badge-ai-demo" title={lang === 'fr' ? 'IA non utilisée (clé manquante ou budget dépassé) — clips générés en mode démo' : 'AI not used (missing key or budget exceeded) — demo mode'}>Demo mode</span>
            ) : null}
          </div>
        </div>
        <div className="lg:col-span-4 grid grid-cols-2 gap-3">
          <div className="p-4 rounded-xl border border-white/5 bg-white/[0.02]">
            <Sparkles className="w-4 h-4 text-[#D4FF00] mb-2" />
            <div className="font-heading text-2xl font-black">{clips.length}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{t('vc.stat.clips')}</div>
          </div>
          <div className="p-4 rounded-xl border border-white/5 bg-white/[0.02]">
            <TrendingUp className="w-4 h-4 text-[#D4FF00] mb-2" />
            <div className="font-heading text-2xl font-black">{avgViral}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{t('vc.stat.viral')}</div>
          </div>
          <div className="p-4 rounded-xl border border-white/5 bg-white/[0.02]">
            <Eye className="w-4 h-4 text-zinc-400 mb-2" />
            <div className="font-heading text-2xl font-black">{clips.filter(c => c.viral_score >= 85).length}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{t('vc.stat.hot')}</div>
          </div>
          <div className="p-4 rounded-xl border border-white/5 bg-white/[0.02]">
            <Clock className="w-4 h-4 text-zinc-400 mb-2" />
            <div className="font-heading text-2xl font-black">{video.processing_time}s</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{t('vc.stat.time')}</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold">{t('vc.list')}</h2>
        <Button variant="ghost" onClick={removeVideo} className="text-zinc-500 hover:text-red-400" data-testid="video-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> {t('vc.delete')}</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="clips-grid">
        {clips.map((c, i) => <ClipCard key={c.id} clip={c} idx={i} />)}
      </div>
    </div>
  );
};

export default VideoClips;
